import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Sparkles, Download, Copy, Edit3, CheckCircle, Loader2, FileText } from 'lucide-react';

const NotesAI = () => {
  const [formData, setFormData] = useState({
    // Loan Details
    loanPurpose: '',
    loanAmount: '',
    propertyValue: '',
    loanType: '',
    
    // Client Details
    clientName: '',
    occupation: '',
    income: '',
    employmentType: '',
    
    // Property Details
    propertyAddress: '',
    propertyType: '',
    
    // Additional Context
    specialCircumstances: '',
    creditHistory: '',
    mitigations: ''
  });

  const [generatedNotes, setGeneratedNotes] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedNotes, setEditedNotes] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });

  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 4000);
  };

  const generateNotes = async () => {
    // Validate minimum required fields
    if (!formData.clientName || !formData.loanAmount || !formData.loanPurpose) {
      showMessage('error', 'Please fill in at least Client Name, Loan Amount, and Loan Purpose');
      return;
    }

    setIsGenerating(true);
    setGeneratedNotes('');
    setIsEditing(false);

    try {
      const response = await fetch(`${backendUrl}/api/notes-ai/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const data = await response.json();
        setGeneratedNotes(data.notes);
        setEditedNotes(data.notes);
        showMessage('success', 'Broker notes generated successfully!');
      } else {
        const errorData = await response.json();
        showMessage('error', errorData.detail || 'Failed to generate notes');
      }
    } catch (error) {
      console.error('Error generating notes:', error);
      showMessage('error', 'Failed to generate notes. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const copyToClipboard = () => {
    const textToCopy = isEditing ? editedNotes : generatedNotes;
    navigator.clipboard.writeText(textToCopy);
    showMessage('success', 'Notes copied to clipboard!');
  };

  const downloadPDF = async () => {
    try {
      const notesToDownload = isEditing ? editedNotes : generatedNotes;
      
      const response = await fetch(`${backendUrl}/api/notes-ai/download-pdf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          notes: notesToDownload,
          clientName: formData.clientName 
        })
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Broker_Notes_${formData.clientName || 'Client'}_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        showMessage('success', 'PDF downloaded successfully!');
      } else {
        showMessage('error', 'Failed to download PDF');
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
      showMessage('error', 'Failed to download PDF');
    }
  };

  const handleEditToggle = () => {
    if (!isEditing) {
      setEditedNotes(generatedNotes);
    }
    setIsEditing(!isEditing);
  };

  const saveEdits = () => {
    setGeneratedNotes(editedNotes);
    setIsEditing(false);
    showMessage('success', 'Changes saved!');
  };

  return (
    <div className="min-h-screen flex flex-col bg-page">
      <Header />
      
      <main className="flex-grow container mx-auto px-4 py-8 max-w-7xl">
        {/* Hero Section */}
        <div className="mb-8 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Sparkles className="w-10 h-10 text-purple-600" />
            <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-purple-600 via-pink-500 to-orange-500 text-transparent bg-clip-text">
              Submission Notes AI
            </h1>
          </div>
          <p className="text-lg text-slate-700 max-w-3xl mx-auto">
            Generate comprehensive broker notes in seconds. Just provide key details, and our AI will create professional loan submission notes following best practice guidelines.
          </p>
        </div>

        {/* Message Alert */}
        {message.text && (
          <div className={`max-w-4xl mx-auto mb-6 flex items-center gap-2 p-3 rounded-lg ${
            message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' :
            'bg-red-50 text-red-700 border border-red-200'
          }`}>
            {message.type === 'success' ? <CheckCircle className="h-5 w-5" /> : <FileText className="h-5 w-5" />}
            {message.text}
          </div>
        )}

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left Column - Input Form */}
          <div>
            <Card className="border-2 border-purple-200 shadow-lg">
              <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50">
                <CardTitle className="text-2xl font-bold text-slate-800 flex items-center gap-2">
                  <FileText className="w-6 h-6 text-purple-600" />
                  Loan Details
                </CardTitle>
                <p className="text-sm text-slate-600 mt-2">Provide brief details - AI will expand them into comprehensive notes</p>
              </CardHeader>
              <CardContent className="pt-6 space-y-6">
                {/* Loan Purpose & Type */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Loan Purpose <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="loanPurpose"
                      value={formData.loanPurpose}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="">Select...</option>
                      <option value="Purchase">Purchase</option>
                      <option value="Refinance">Refinance</option>
                      <option value="Construction">Construction</option>
                      <option value="Equity Release">Equity Release</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Loan Type
                    </label>
                    <select
                      name="loanType"
                      value={formData.loanType}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="">Select...</option>
                      <option value="Owner Occupied">Owner Occupied</option>
                      <option value="Investment">Investment</option>
                    </select>
                  </div>
                </div>

                {/* Loan Amount & Property Value */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Loan Amount <span className="text-red-500">*</span>
                    </label>
                    <Input
                      type="text"
                      name="loanAmount"
                      placeholder="$500,000"
                      value={formData.loanAmount}
                      onChange={handleInputChange}
                      className="border-slate-300 focus:ring-purple-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Property Value
                    </label>
                    <Input
                      type="text"
                      name="propertyValue"
                      placeholder="$625,000"
                      value={formData.propertyValue}
                      onChange={handleInputChange}
                      className="border-slate-300 focus:ring-purple-500"
                    />
                  </div>
                </div>

                {/* Client Details */}
                <div className="border-t border-slate-200 pt-6">
                  <h3 className="text-lg font-bold text-slate-800 mb-4">Client Information</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        Client Name(s) <span className="text-red-500">*</span>
                      </label>
                      <Input
                        type="text"
                        name="clientName"
                        placeholder="John & Mary Smith"
                        value={formData.clientName}
                        onChange={handleInputChange}
                        className="border-slate-300 focus:ring-purple-500"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                          Occupation
                        </label>
                        <Input
                          type="text"
                          name="occupation"
                          placeholder="Engineer, Teacher"
                          value={formData.occupation}
                          onChange={handleInputChange}
                          className="border-slate-300 focus:ring-purple-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                          Employment Type
                        </label>
                        <select
                          name="employmentType"
                          value={formData.employmentType}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                        >
                          <option value="">Select...</option>
                          <option value="PAYG Full-time">PAYG Full-time</option>
                          <option value="PAYG Part-time">PAYG Part-time</option>
                          <option value="PAYG Casual">PAYG Casual</option>
                          <option value="Self-employed">Self-employed</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        Annual Income (combined)
                      </label>
                      <Input
                        type="text"
                        name="income"
                        placeholder="$120,000"
                        value={formData.income}
                        onChange={handleInputChange}
                        className="border-slate-300 focus:ring-purple-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Property Details */}
                <div className="border-t border-slate-200 pt-6">
                  <h3 className="text-lg font-bold text-slate-800 mb-4">Property Details</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        Property Address
                      </label>
                      <Input
                        type="text"
                        name="propertyAddress"
                        placeholder="123 Main Street, Melbourne VIC 3000"
                        value={formData.propertyAddress}
                        onChange={handleInputChange}
                        className="border-slate-300 focus:ring-purple-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        Property Type
                      </label>
                      <select
                        name="propertyType"
                        value={formData.propertyType}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      >
                        <option value="">Select...</option>
                        <option value="House">House (Detached)</option>
                        <option value="Townhouse">Townhouse</option>
                        <option value="Unit">Unit</option>
                        <option value="Apartment">Apartment</option>
                        <option value="Land">Land Only</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Additional Context */}
                <div className="border-t border-slate-200 pt-6">
                  <h3 className="text-lg font-bold text-slate-800 mb-4">Additional Information (Optional)</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        Credit History Notes
                      </label>
                      <textarea
                        name="creditHistory"
                        placeholder="e.g., Clean credit file, one late payment 2 years ago due to overseas travel"
                        value={formData.creditHistory}
                        onChange={handleInputChange}
                        rows="2"
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        Special Circumstances
                      </label>
                      <textarea
                        name="specialCircumstances"
                        placeholder="e.g., Recent job change, expecting inheritance, child support payments"
                        value={formData.specialCircumstances}
                        onChange={handleInputChange}
                        rows="2"
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        Risk Mitigations
                      </label>
                      <textarea
                        name="mitigations"
                        placeholder="e.g., Genuine savings demonstrated, strong rental history, excellent serviceability"
                        value={formData.mitigations}
                        onChange={handleInputChange}
                        rows="2"
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Generate Button */}
                <Button
                  onClick={generateNotes}
                  disabled={isGenerating}
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold py-6 text-lg"
                >
                  {isGenerating ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Generating Smart Notes...
                    </>
                  ) : (
                    <>
                      <Sparkles className="mr-2 h-5 w-5" />
                      Generate Broker Notes with AI
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Generated Notes */}
          <div>
            <Card className="border-2 border-purple-200 shadow-lg sticky top-4">
              <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-2xl font-bold text-slate-800 flex items-center gap-2">
                    <FileText className="w-6 h-6 text-purple-600" />
                    Generated Broker Notes
                  </CardTitle>
                  {generatedNotes && (
                    <div className="flex gap-2">
                      <Button
                        onClick={handleEditToggle}
                        variant="outline"
                        size="sm"
                        className="border-purple-300 text-purple-700 hover:bg-purple-50"
                      >
                        <Edit3 className="w-4 h-4 mr-1" />
                        {isEditing ? 'Cancel' : 'Edit'}
                      </Button>
                      {isEditing && (
                        <Button
                          onClick={saveEdits}
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 text-white"
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Save
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                {!generatedNotes && !isGenerating ? (
                  <div className="text-center py-16">
                    <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500 text-lg">
                      Fill in the form and click "Generate Broker Notes with AI" to create comprehensive loan submission notes
                    </p>
                  </div>
                ) : isGenerating ? (
                  <div className="text-center py-16">
                    <Loader2 className="w-16 h-16 text-purple-600 mx-auto mb-4 animate-spin" />
                    <p className="text-slate-700 text-lg font-semibold">
                      AI is generating your broker notes...
                    </p>
                    <p className="text-slate-500 text-sm mt-2">
                      This usually takes 10-15 seconds
                    </p>
                  </div>
                ) : (
                  <div>
                    {isEditing ? (
                      <textarea
                        value={editedNotes}
                        onChange={(e) => setEditedNotes(e.target.value)}
                        className="w-full h-[600px] p-4 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 font-mono text-sm"
                      />
                    ) : (
                      <div className="bg-slate-50 rounded-lg p-6 max-h-[600px] overflow-y-auto border border-slate-200">
                        <pre className="whitespace-pre-wrap font-sans text-sm text-slate-800 leading-relaxed">
                          {generatedNotes}
                        </pre>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex gap-3 mt-6">
                      <Button
                        onClick={copyToClipboard}
                        variant="outline"
                        className="flex-1 border-purple-300 text-purple-700 hover:bg-purple-50"
                      >
                        <Copy className="w-4 h-4 mr-2" />
                        Copy to Clipboard
                      </Button>
                      <Button
                        onClick={downloadPDF}
                        className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download PDF
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default NotesAI;
